import { z } from "zod";

/**
 * Control-flow socket constants
 * These are implicit sockets added to every non-trigger node for execution ordering.
 * Flow sockets carry no data, just completion signals with AND-join semantics.
 */
export const FLOW_IN_SOCKET_ID = "__flow_control_in__";
export const FLOW_OUT_SOCKET_ID = "__flow_control_out__";
export const FLOW_CONTROL_SOCKET_TYPE = "flow_control" as const;

/**
 * Check if a socket ID is a flow socket
 */
export function isFlowSocket(socketId: string): boolean {
  return socketId === FLOW_IN_SOCKET_ID || socketId === FLOW_OUT_SOCKET_ID;
}

/**
 * Check if a socket type indicates it expects an array.
 * Array socket types start with "array<" (e.g., "array<string>", "array<entity_reference:TYPE>")
 */
export function isArraySocketType(socketType: string | undefined): boolean {
  if (!socketType) return false;
  return socketType.startsWith("array<");
}

/**
 * Base socket types - defines what can flow between nodes
 */
export const BaseSocketTypes = [
  "string",
  "number",
  "boolean",
  "date",
  "json",
  "uuid",
  "entity_reference",
  "array<string>",
  "array<number>",
  "array<entity_reference>",
  "any",
  FLOW_CONTROL_SOCKET_TYPE, // Control-flow socket type for execution ordering
] as const;

/**
 * Socket type schema - allows base types and typed entity references
 * Examples: "string", "entity_reference", "entity_reference:AP_INVOICE", "array<entity_reference:CORE_DOCUMENT>"
 */
export const SocketTypeSchema = z.union([
  z.enum(BaseSocketTypes),
  // Typed entity reference (e.g., "entity_reference:AP_INVOICE")
  z.string().regex(/^entity_reference:[A-Z_]+$/),
  // Typed array of entity references (e.g., "array<entity_reference:CORE_DOCUMENT>")
  z.string().regex(/^array<entity_reference:[A-Z_]+>$/),
]);

export type SocketType = z.infer<typeof SocketTypeSchema>;

/**
 * Socket definition
 */
export const SocketDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: SocketTypeSchema,
  required: z.boolean().prefault(true),
  description: z.string().optional(),
});

export type SocketDefinition = z.infer<typeof SocketDefinitionSchema>;

/**
 * Step category for UI organization
 */
export const StepCategorySchema = z.enum([
  "Data Access",
  "Transformation",
  "Integration",
  "Control Flow",
  "Utility",
  "Trigger",
]);

export type StepCategory = z.infer<typeof StepCategorySchema>;

/**
 * Step definition
 */
export const StepDefinitionSchema = z.object({
  id: z.uuid(),
  type: z.string(),
  name: z.string(),
  category: StepCategorySchema,
  config: z.record(z.string(), z.unknown()),
  sockets: z.object({
    inputs: z.array(SocketDefinitionSchema),
    outputs: z.array(SocketDefinitionSchema),
  }),
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
});

export type StepDefinition = z.infer<typeof StepDefinitionSchema>;

/**
 * Edge definition
 */
export const EdgeDefinitionSchema = z.object({
  id: z.uuid(),
  sourceStepId: z.uuid(),
  sourceSocketId: z.string(),
  targetStepId: z.uuid(),
  targetSocketId: z.string(),
});

export type EdgeDefinition = z.infer<typeof EdgeDefinitionSchema>;

/**
 * Workflow run status
 */
export const WorkflowRunStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
]);

export type WorkflowRunStatus = z.infer<typeof WorkflowRunStatusSchema>;

/**
 * Step run status
 */
export const StepRunStatusSchema = z.enum([
  "pending",
  "queued",
  "running",
  "completed",
  "failed",
  "skipped",
  "timed_out",
  "cancelled",
]);

export type StepRunStatus = z.infer<typeof StepRunStatusSchema>;

/**
 * Workflow retry configuration
 */
export const WorkflowRetryConfigSchema = z.object({
  enabled: z.boolean().prefault(false),
  maxRetries: z.number().prefault(0),
  initialDelay: z.number().prefault(1000),
  maxDelay: z.number().prefault(60000),
  backoffMultiplier: z.number().prefault(2),
});

export type WorkflowRetryConfig = z.infer<typeof WorkflowRetryConfigSchema>;
